import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  X,
  Search,
  Play,
  Pause,
  Plus,
  Minus,
  ChevronUp,
  ChevronDown,
  Layers,
  AlertTriangle,
  Shuffle,
  Loader2,
} from "lucide-react";
import type { SoundResponse, ComboResponse } from "@wizard-sounds/shared";
import { COMBO_MAX_SOUNDS, COMBO_MIN_SOUNDS } from "@wizard-sounds/shared";
import { useAudio } from "@/components/audio-provider";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useScrollLock } from "@/lib/use-scroll-lock";

interface ComboBuilderModalProps {
  limitRemaining: number;
  onClose: () => void;
  onCreated: (combo: ComboResponse) => void;
}

export function ComboBuilderModal({
  limitRemaining,
  onClose,
  onCreated,
}: ComboBuilderModalProps) {
  useScrollLock();
  const { play, stop, isPlaying, playCombo, activeComboId, stopAll } = useAudio();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState("");
  const tagInputRef = useRef<HTMLInputElement>(null);
  const [queue, setQueue] = useState<SoundResponse[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch sounds from the API directly instead of relying on parent's paginated list
  const [allSounds, setAllSounds] = useState<SoundResponse[]>([]);
  const [searchResults, setSearchResults] = useState<SoundResponse[] | null>(null);
  const [loadingSounds, setLoadingSounds] = useState(true);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  // Load initial popular sounds on mount
  useEffect(() => {
    api.sounds.list({ sort: "popular", limit: 200 })
      .then((res) => setAllSounds(res.sounds))
      .catch(() => {})
      .finally(() => setLoadingSounds(false));
  }, []);

  // Debounced search against the API
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);

    if (!searchQuery.trim()) {
      setSearchResults(null);
      setSearching(false);
      return;
    }

    setSearching(true);
    searchTimer.current = setTimeout(() => {
      api.sounds.list({ q: searchQuery.trim(), limit: 50 })
        .then((res) => setSearchResults(res.sounds))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);

    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery]);

  const displaySounds = searchResults ?? allSounds;

  const queueIds = useMemo(() => new Set(queue.map((s) => s.id)), [queue]);

  // Preload audio files when sounds are added to the queue so combo preview is instant
  const preloadedUrls = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const sound of queue) {
      if (preloadedUrls.current.has(sound.audio_url)) continue;
      preloadedUrls.current.add(sound.audio_url);
      // Fetch warms the browser HTTP cache (audio has immutable cache headers)
      fetch(sound.audio_url).catch(() => {});
    }
  }, [queue]);

  const addToQueue = useCallback(
    (sound: SoundResponse) => {
      if (queue.length >= COMBO_MAX_SOUNDS || queueIds.has(sound.id)) return;
      setQueue((prev) => [...prev, sound]);
    },
    [queue.length, queueIds],
  );

  const removeFromQueue = useCallback((index: number) => {
    setQueue((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const moveUp = useCallback((index: number) => {
    if (index === 0) return;
    setQueue((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  }, []);

  const moveDown = useCallback((index: number) => {
    setQueue((prev) => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  }, []);

  const handleRandomCombo = useCallback(() => {
    if (allSounds.length < COMBO_MIN_SOUNDS) return;
    stopAll();
    // Pick 2-3 random sounds
    const count = Math.random() < 0.5 ? 2 : 3;
    const shuffled = [...allSounds].sort(() => Math.random() - 0.5);
    const picked = shuffled.slice(0, Math.min(count, shuffled.length));
    setQueue(picked);
    setName("");
  }, [allSounds, stopAll]);

  const isPreviewPlaying = activeComboId === "preview";

  const handlePreview = useCallback(() => {
    if (queue.length === 0) return;
    if (isPreviewPlaying) {
      stopAll();
      return;
    }
    playCombo(
      "preview",
      name || "Preview",
      queue.map((s) => ({
        id: s.id,
        audioUrl: s.audio_url,
        title: s.title,
        durationMs: s.duration_ms,
        slug: s.slug,
      })),
    );
  }, [queue, name, playCombo, stopAll, isPreviewPlaying]);

  const handleSubmit = async () => {
    if (!name.trim() || queue.length < COMBO_MIN_SOUNDS) return;
    setSaving(true);
    setError(null);

    try {
      // Include any text still being typed as a final tag
      const finalTags = [...tags];
      const leftover = tagDraft.trim();
      if (leftover && !finalTags.includes(leftover.toLowerCase())) {
        finalTags.push(leftover);
      }

      const created = await api.combos.create({
        name: name.trim(),
        description: description.trim() || undefined,
        tags: finalTags.length > 0 ? finalTags : undefined,
        sound_ids: queue.map((s) => s.id),
      });
      stopAll();
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create combo");
    }
    setSaving(false);
  };

  const canSubmit = name.trim() && queue.length >= COMBO_MIN_SOUNDS && queue.length <= COMBO_MAX_SOUNDS;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/60 p-2 backdrop-blur-sm sm:py-8 sm:px-4">
      <div className="mx-0 flex w-full max-w-2xl flex-col rounded-[18px] border border-mist bg-slate shadow-xl max-h-[calc(100dvh-1rem)] sm:mx-4 sm:max-h-[calc(100dvh-4rem)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-mist px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-spark/15">
              <Layers className="h-4 w-4 text-spark" />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold tracking-display text-parchment">
                Create Combo
              </h2>
              <p className="text-xs text-fog">{limitRemaining} combos remaining</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-fog hover:bg-slate-raised">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          {/* Name + Random */}
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Combo name"
              className="min-w-0 flex-1 rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment placeholder:text-fog/50 outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
            />
            <button
              onClick={handleRandomCombo}
              disabled={loadingSounds || allSounds.length < COMBO_MIN_SOUNDS}
              className="flex shrink-0 items-center gap-1.5 rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-fog transition-colors hover:border-spark hover:text-spark disabled:opacity-40"
              title="Build a random combo"
            >
              <Shuffle className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Random</span>
            </button>
          </div>

          {/* Queue */}
          <div>
            <h3 className="mb-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              Sequence ({queue.length}/{COMBO_MAX_SOUNDS})
            </h3>

            {queue.length === 0 ? (
              <div className="rounded-xl border border-dashed border-mist p-4 text-center text-sm text-fog">
                Add {COMBO_MIN_SOUNDS}-{COMBO_MAX_SOUNDS} sounds below, or hit Random
              </div>
            ) : (
              <div className="space-y-1">
                {queue.map((sound, i) => (
                  <div
                    key={`${sound.id}-${i}`}
                    className="flex items-center gap-2 rounded-xl bg-ink/50 px-3 py-2"
                  >
                    <span className="font-mono text-[11px] font-bold text-spark">{i + 1}</span>
                    <button
                      onClick={() => {
                        if (isPlaying(sound.id)) stop(sound.id);
                        else play(sound.id, sound.audio_url, { title: sound.title, durationMs: sound.duration_ms, slug: sound.slug });
                      }}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-raised"
                    >
                      {isPlaying(sound.id) ? (
                        <Pause className="h-2.5 w-2.5 text-parchment" />
                      ) : (
                        <Play className="ml-0.5 h-2.5 w-2.5 text-parchment" />
                      )}
                    </button>
                    <span className="min-w-0 flex-1 truncate text-sm text-parchment">
                      {sound.title}
                    </span>
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => moveUp(i)}
                        disabled={i === 0}
                        className="rounded p-1 text-fog hover:text-parchment disabled:opacity-30 sm:p-0.5"
                      >
                        <ChevronUp className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                      </button>
                      <button
                        onClick={() => moveDown(i)}
                        disabled={i === queue.length - 1}
                        className="rounded p-1 text-fog hover:text-parchment disabled:opacity-30 sm:p-0.5"
                      >
                        <ChevronDown className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                      </button>
                      <button
                        onClick={() => removeFromQueue(i)}
                        className="rounded p-1 text-fog hover:text-ember sm:p-0.5"
                      >
                        <Minus className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {queue.length >= COMBO_MIN_SOUNDS && (
              <button
                onClick={handlePreview}
                className={cn(
                  "mt-2 flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors",
                  isPreviewPlaying
                    ? "border-spark bg-spark/15 text-spark"
                    : "border-mist text-parchment hover:border-spark hover:bg-spark/5 hover:text-spark",
                )}
              >
                {isPreviewPlaying ? (
                  <>
                    <Pause className="h-4 w-4" /> Stop Preview
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" /> Preview Combo
                  </>
                )}
              </button>
            )}
          </div>

          {/* Sound Search */}
          <div>
            <h3 className="mb-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              Add sounds
            </h3>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fog" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search all sounds..."
                className="w-full rounded-xl border border-mist bg-ink py-2 pl-9 pr-3 text-sm text-parchment placeholder:text-fog/50 outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-fog" />
              )}
            </div>
            <div className="max-h-36 overflow-y-auto rounded-xl border border-mist sm:max-h-48">
              {loadingSounds ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-fog" />
                </div>
              ) : displaySounds.length === 0 ? (
                <div className="p-3 text-center text-sm text-fog">
                  {searchQuery.trim() ? "No sounds found" : "No sounds available"}
                </div>
              ) : (
                displaySounds.slice(0, 50).map((sound) => {
                  const inQueue = queueIds.has(sound.id);
                  return (
                    <button
                      key={sound.id}
                      onClick={() => addToQueue(sound)}
                      disabled={inQueue || queue.length >= COMBO_MAX_SOUNDS}
                      className={cn(
                        "flex w-full items-center gap-2 border-b border-mist px-3 py-2 text-left text-sm transition-colors last:border-0",
                        inQueue
                          ? "bg-spark/5 text-fog"
                          : queue.length >= COMBO_MAX_SOUNDS
                            ? "text-fog/50"
                            : "text-parchment hover:bg-slate-raised",
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">{sound.title}</span>
                      {sound.tags.length > 0 && (
                        <span className="hidden shrink-0 text-[10px] text-fog sm:block">
                          {sound.tags
                            .slice(0, 2)
                            .map((t) => t.name)
                            .join(", ")}
                        </span>
                      )}
                      {inQueue ? (
                        <span className="shrink-0 font-mono text-[10px] text-spark">added</span>
                      ) : (
                        <Plus className="h-3.5 w-3.5 shrink-0 text-fog" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="mb-1 block font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              Tags
            </label>
            <div
              className="flex min-h-[38px] flex-wrap items-center gap-1.5 rounded-xl border border-mist bg-ink px-2 py-1.5 focus-within:border-spark focus-within:ring-2 focus-within:ring-spark/20"
              onClick={() => tagInputRef.current?.focus()}
            >
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="flex items-center gap-1 rounded-lg bg-spark/15 px-2 py-0.5 text-xs text-spark"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTags((prev) => prev.filter((t) => t !== tag));
                    }}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-spark/20"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              ))}
              <input
                ref={tagInputRef}
                type="text"
                value={tagDraft}
                onChange={(e) => {
                  const val = e.target.value;
                  // If the user typed a comma, commit the tag
                  if (val.includes(",")) {
                    const parts = val.split(",");
                    for (const part of parts) {
                      const trimmed = part.trim().toLowerCase();
                      if (trimmed && !tags.includes(trimmed)) {
                        setTags((prev) => [...prev, trimmed]);
                      }
                    }
                    setTagDraft("");
                    return;
                  }
                  setTagDraft(val);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Tab" || e.key === "Enter") {
                    const trimmed = tagDraft.trim().toLowerCase();
                    if (trimmed && !tags.includes(trimmed)) {
                      e.preventDefault();
                      setTags((prev) => [...prev, trimmed]);
                      setTagDraft("");
                    } else if (trimmed) {
                      // Duplicate — just clear
                      e.preventDefault();
                      setTagDraft("");
                    }
                  } else if (e.key === "Backspace" && !tagDraft && tags.length > 0) {
                    // Remove last tag when backspacing on empty input
                    setTags((prev) => prev.slice(0, -1));
                  }
                }}
                placeholder={tags.length === 0 ? "Type and press comma or tab" : ""}
                className="min-w-[80px] flex-1 bg-transparent px-1 py-0.5 text-sm text-parchment placeholder:text-fog/50 outline-none"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
              className="w-full rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment placeholder:text-fog/50 outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 rounded-xl bg-ember/10 px-3 py-2 text-sm text-ember">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {limitRemaining === 0 && (
            <div className="flex items-center gap-2 rounded-xl bg-ember/10 px-3 py-2 text-sm text-ember">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Combo limit reached. Delete an existing combo first.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center gap-2 border-t border-mist px-4 py-3 sm:justify-end sm:px-6 sm:py-4">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl px-4 py-2.5 text-sm text-fog hover:bg-slate-raised sm:flex-none sm:py-2"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || saving || limitRemaining === 0}
            className="flex-1 rounded-xl bg-spark px-5 py-2.5 text-sm font-medium text-ink hover:bg-spark-soft disabled:opacity-50 sm:flex-none sm:py-2"
          >
            {saving ? "Creating..." : "Create Combo"}
          </button>
        </div>
      </div>
    </div>
  );
}
